/**
 * Finance · Overview.
 */
import { TrendingUp, Sparkles, Wallet } from "lucide-react";
import { Card, SectionHeader, DeltaChip, SeverityBadge } from "../../components/lib";
import { ListRow } from "../../components/lib.overlay";
import { CanonicalKpiCell } from "../../components/CanonicalKpiCell";
import { useCanonicalKpi } from "../../hooks/useCanonicalKpi";
import { useNavigate } from "react-router-dom";
import { FINANCE_OVERVIEW, CASH_FLOW, CASH_FORECAST_START_INDEX, FIN_RISKS, REVENUE_STREAMS, EXPENSE_TOTALS } from "../../mocks/financeData";

export default function Overview() {
  const navigate = useNavigate();
  const { data: kpi, loading, error } = useCanonicalKpi("orvan");
  const totalRev  = REVENUE_STREAMS.reduce((n, s) => n + s.monthEGP, 0);
  const totalExp  = EXPENSE_TOTALS.reduce((n, s) => n + s.monthEGP, 0);
  const openRisks = FIN_RISKS.filter((r) => r.status !== "mitigated");
  const maxBal = Math.max(...CASH_FLOW.map((c) => c.balance));
  const legacyKpis = (FINANCE_OVERVIEW.kpis || []).filter((k) => k.key !== "revenue" && k.key !== "revenueMTD");

  return (
    <div className="space-y-6" data-testid="fn-overview">
      <Card testId="fn-kpis">
        <SectionHeader eyebrow="Health" title="Financial Performance"
          action={<span className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-ink-muted)" }} data-testid="fn-kpi-source-tag">Source: /api/pureh/kpi/summary</span>} />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <CanonicalKpiCell
            testId="fn-kpi-revenue"
            label="Revenue (MTD)"
            value={loading ? null : (kpi?.revenue.monthlyEGP.value ?? 0)}
            unit="EGP"
            source={kpi?.revenue.sourceCollection || "pureh_customer_payments"}
            footnote={error ? "endpoint unavailable" : (kpi?.dataMode === "demo" ? "demo mode" : undefined)}
            empty={loading}
          />
          <CanonicalKpiCell
            testId="fn-kpi-orders-30d"
            label="Orders · 30d"
            value={loading ? null : (kpi?.orders.last30d.value ?? 0)}
            unit={null}
            source={kpi?.orders.sourceCollection || "pureh_orders"}
          />
          <CanonicalKpiCell
            testId="fn-kpi-approvals"
            label="Pending approvals"
            value={loading ? null : (kpi?.approvals.totalPending.value ?? 0)}
            unit={null}
            source={kpi?.approvals.sourceCollection || "pureh_approvals"}
          />
          {legacyKpis.slice(0, 3).map((k) => (
            <div key={k.key} data-testid={`fn-kpi-${k.key}`}>
              <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-ink-muted)" }}>{k.label}</div>
              <div className="mt-1 text-xl font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)", letterSpacing: "-0.01em" }}>{k.value}</div>
              <div className="mt-1 flex items-center gap-2">
                <DeltaChip delta={k.delta} trend={k.trend as any} />
                {k.hint && <span className="text-[10px]" style={{ color: "var(--ph-ink-soft)" }}>{k.hint}</span>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card testId="fn-cash-mini" className="lg:col-span-2">
          <SectionHeader eyebrow="6-month view" title="Cash balance" action={<TrendingUp size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          <div className="flex items-end gap-3 h-40 pt-2" data-testid="fn-cash-bars">
            {CASH_FLOW.map((c, i) => {
              const isForecast = i >= CASH_FORECAST_START_INDEX;
              return (
                <div key={c.month} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full flex items-end" style={{ height: 130 }}>
                    <div
                      style={{
                        width: "100%",
                        height: `${(c.balance / maxBal) * 100}%`,
                        background: isForecast ? "var(--ph-brand-accent)" : "var(--ph-brand-primary)",
                        borderRadius: "var(--ph-radius-sm) var(--ph-radius-sm) 0 0",
                        opacity: isForecast ? 0.6 : 1,
                      }}
                    />
                  </div>
                  <div className="text-[10px] font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>{(c.balance / 1000).toFixed(1)}M</div>
                  <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: isForecast ? "var(--ph-brand-accent)" : "var(--ph-ink-muted)" }}>{c.month}{isForecast ? "*" : ""}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-[10px]" style={{ color: "var(--ph-ink-muted)" }}>* forecast</div>
        </Card>

        <Card testId="fn-inout">
          <SectionHeader eyebrow="This month" title="In vs Out" />
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Revenue in</div>
              <div className="text-lg font-semibold tabular-nums" style={{ color: "var(--ph-success)" }}>EGP {(totalRev / 1000000).toFixed(2)}M</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Expenses out</div>
              <div className="text-lg font-semibold tabular-nums" style={{ color: "var(--ph-danger)" }}>EGP {(totalExp / 1000000).toFixed(2)}M</div>
            </div>
            <div style={{ borderTop: "1px solid var(--ph-border-subtle)", paddingTop: 12 }}>
              <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Net</div>
              <div className="text-lg font-semibold tabular-nums" style={{ color: "var(--ph-brand-primary)" }}>EGP {((totalRev - totalExp) / 1000000).toFixed(2)}M</div>
            </div>
          </div>
        </Card>
      </div>

      <Card testId="fn-open-risks">
        <SectionHeader eyebrow="Register" title="Open financial risks" action={<Sparkles size={14} style={{ color: "var(--ph-ai)" }} />} />
        <div className="space-y-1">
          {openRisks.slice(0, 4).map((r) => (
            <ListRow
              key={r.id}
              testId={`fn-risk-mini-${r.id}`}
              onClick={() => navigate("/pureh/finance/risks")}
              label={r.title}
              sublabel={<>Exposure {r.exposureEGP > 0 ? `EGP ${r.exposureEGP.toLocaleString()}` : "—"} · <span style={{ color: "var(--ph-ai)" }}>AI {Math.round(r.aiConfidence * 100)}%</span></>}
              trailing={<SeverityBadge severity={r.severity} />}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}
